import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCommunicationCenter } from '../../../app/providers/CommunicationCenterContext';
import { useFeatureServices } from '../../../shared/hooks/use-feature-services';
import { useMyVwContext } from '../../../shared/hooks/use-my-vw-context';
import { useToast } from '../../../shared/context/ToastContext';
import { useUser } from '../../../app/providers/UserContext';
import { buildConsentPayload, CheckboxState } from '../utils/buildConsentPayload';
import { TabData } from '../types/types';

/**
 * Builds a fresh CheckboxState snapshot from the context's tabs — this is
 * the "last known good" baseline: either what the backend last reported, or
 * (after a save) what was just successfully submitted.
 */
const buildCheckboxStateFromTabs = (tabs: TabData[]): CheckboxState => {
  return tabs.reduce<CheckboxState>((acc, tab) => {
    tab.purposes.forEach((purpose) => {
      acc[purpose.purposeId] = purpose.channels.reduce<Record<string, boolean>>(
        (channelAcc, channel) => {
          channelAcc[channel.channelName] = channel.isOptedIn;
          return channelAcc;
        },
        {},
      );
    });
    return acc;
  }, {});
};

/**
 * Owns the editable draft state for the Communication Center checkboxes,
 * plus the Save/Cancel/dirty-tracking behavior on top of it.
 *
 * Split out from CommunicationCenterContext deliberately: the context owns
 * FETCHED data (survives navigation, shared app-wide), this hook owns
 * IN-PROGRESS EDITS (scoped to however long the page is mounted — there's no
 * requirement to preserve an unsaved draft across navigation).
 */
export const useCommunicationCenterForm = () => {
  const {
    tabs,
    isLoading,
    isConsentDataSettled,
    communicationChannels,
    userConsentData,
    hasExistingConsent,
    userId,
    applyOptimisticConsentUpdate,
  } = useCommunicationCenter();
  const { user } = useUser();
  const { authService, translationHandler } = useMyVwContext();
  const { showToast } = useToast();
  const { 'locale-service': localeService } = useFeatureServices();
  const localeCode = `${localeService.language}-${localeService.countryCode}`;

  // The last known good state — either the initial fetch, or the last
  // successful save. Toggling a checkbox never mutates this; it's the
  // "return to" target for Cancel and the "is anything different" reference
  // for isDirty.
  const [baseline, setBaseline] = useState<CheckboxState>({});
  // The user's in-progress edits. Starts equal to baseline, diverges as the
  // user toggles checkboxes.
  const [checkboxState, setCheckboxState] = useState<CheckboxState>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // Seeded once both the tab structure AND the user's real consent data have
  // settled — seeding any earlier risks building a baseline off an
  // "all-unchecked" snapshot right before the real data lands, which would
  // then silently stomp on whatever the user had already started toggling.
  const [hasSeeded, setHasSeeded] = useState<boolean>(false);

  useEffect(() => {
    if (hasSeeded || isLoading || !isConsentDataSettled || tabs.length === 0) return;

    const initialState = buildCheckboxStateFromTabs(tabs);
    setBaseline(initialState);
    setCheckboxState(initialState);
    setHasSeeded(true);
  }, [hasSeeded, isLoading, isConsentDataSettled, tabs]);

  const toggleChannel = useCallback((purposeId: string, channelName: string) => {
    setCheckboxState((previous) => ({
      ...previous,
      [purposeId]: {
        ...previous[purposeId],
        [channelName]: !previous[purposeId]?.[channelName],
      },
    }));
  }, []);

  const handleCancel = useCallback(() => {
    setCheckboxState(baseline);
  }, [baseline]);

  // Deep-compare against the baseline — this is what makes "check then
  // uncheck the same box" correctly disable the CTAs again, rather than a
  // naive "has anything been touched" flag that would stay stuck on true.
  const isDirty = useMemo(
    () => JSON.stringify(checkboxState) !== JSON.stringify(baseline),
    [checkboxState, baseline],
  );

  const handleSave = useCallback(async () => {
    if (!userId) return;

    // Collect only the purposes where the user actually made a change — there
    // is no point sending a request for a purpose the user never touched.
    const dirtyPurposes: Array<{ tabLabel: string; purposeId: string }> = [];

    tabs.forEach((tab) => {
      tab.purposes.forEach((purpose) => {
        const current = checkboxState[purpose.purposeId] ?? {};
        const base = baseline[purpose.purposeId] ?? {};

        if (JSON.stringify(current) !== JSON.stringify(base)) {
          dirtyPurposes.push({ tabLabel: tab.label, purposeId: purpose.purposeId });
        }
      });
    });

    if (dirtyPurposes.length === 0) return;

    setIsSaving(true);

    const results = await Promise.allSettled(
      dirtyPurposes.map(({ purposeId }) => {
        const payload = buildConsentPayload({
          user,
          userId,
          tabs: tabs.map((tab) => ({
            ...tab,
            purposes: tab.purposes.filter((p) => p.purposeId === purposeId),
          })).filter((tab) => tab.purposes.length > 0),
          checkboxState,
          communicationChannels,
          userConsentData,
          isUpdate: hasExistingConsent,
        });

        return authService
          .createUserConsentData(payload.input, localeCode)
          .then((response) => {
            if (!response?.createCustomerConsent) {
              throw new Error('createUserConsentData did not return a successful response');
            }

            return payload.input.consents;
          });
      }),
    );

    type PurposeResult = {
      tabLabel: string;
      purposeId: string;
      status: 'fulfilled' | 'rejected';
      consents?: ReturnType<typeof buildConsentPayload>['input']['consents'];
    };

    const purposeResults: PurposeResult[] = results.map((result, index) => ({
      ...dirtyPurposes[index],
      status: result.status,
      consents: result.status === 'fulfilled' ? result.value : undefined,
    }));

    const succeeded = purposeResults.filter((r) => r.status === 'fulfilled');
    const failed = purposeResults.filter((r) => r.status === 'rejected');

    // Apply optimistic update and advance the baseline only for the purposes
    // that actually succeeded — leaving failed ones still dirty so the user
    // can retry without losing their in-progress change.
    if (succeeded.length > 0) {
      const succeededConsents = succeeded.flatMap((r) => r.consents ?? []);

      applyOptimisticConsentUpdate(succeededConsents);

      setBaseline((previousBaseline) => {
        const next = { ...previousBaseline };

        succeeded.forEach(({ purposeId }) => {
          next[purposeId] = { ...checkboxState[purposeId] };
        });

        return next;
      });
    }

    if (failed.length === 0) {
      // Every dirty purpose saved successfully.
      showToast(translationHandler('communicationCenter.save.success'), 'Success');
    } else if (succeeded.length === 0) {
      // Every dirty purpose failed — show the generic error.
      console.error('Error saving communication center preferences: all purposes failed');
      showToast(translationHandler('communicationCenter.save.error'), 'Alert');
    } else {
      // Partial failure — one toast per failed tab so the user knows exactly
      // which category didn't save.
      failed.forEach(({ tabLabel }) => {
        console.error(`Error saving communication center preferences for tab: ${tabLabel}`);
        const message = translationHandler('communicationCenter.save.partialError').replace(
          '{tabName}',
          tabLabel,
        );
        showToast(message, 'Alert');
      });
    }

    setIsSaving(false);
  }, [
    userId,
    user,
    tabs,
    checkboxState,
    baseline,
    communicationChannels,
    userConsentData,
    hasExistingConsent,
    authService,
    applyOptimisticConsentUpdate,
    showToast,
    translationHandler,
    localeCode,
  ]);

  // What the UI actually renders: the fetched tab structure, with each
  // channel's isOptedIn overridden by the user's in-progress draft instead
  // of the last-fetched value. Computed here so CommunicationCenter.tsx and
  // TopicItem don't need to know CheckboxState's shape at all.
  const tabsWithDraftState = useMemo<TabData[]>(() => {
    return tabs.map((tab) => ({
      ...tab,
      purposes: tab.purposes.map((purpose) => ({
        ...purpose,
        channels: purpose.channels.map((channel) => ({
          ...channel,
          isOptedIn: checkboxState[purpose.purposeId]?.[channel.channelName] ?? channel.isOptedIn,
        })),
      })),
    }));
  }, [tabs, checkboxState]);

  return {
    tabs: tabsWithDraftState,
    toggleChannel,
    handleCancel,
    handleSave,
    isDirty,
    isSaving,
  };
};
